<template>
  <a-spin :spinning="loading">
    <a-modal
      title="Enter Video Resource Url"
      v-model="visibleLinkModal"
      :destroy-on-close="true"
      data-testid="document-form-video-link-modal"
      @cancel="closeLinkModal"
    >
      <a-alert
        message="You need to provide the video link as a resource URL, not as a hosting link (e.g. YouTube)"
        type="info"
        show-icon
      />
      <a-input
        class="mt-4"
        v-model="uploadedLink"
        placeholder="Enter Link"
        data-testid="document-form-video-link-input"
      />

      <template slot="footer">
        <div class="flex justify-content-between" data-testid="document-form-video-link-footer">
          <a-button @click="closeLinkModal" data-testid="document-form-video-link-close-button">Close</a-button>
          <div class="flex justify-content-end">
            <a-button
              icon="form"
              type="primary"
              ghost
              :disabled="!uploadedLink"
              @click="uploadLink"
              data-testid="document-form-video-link-submit-button"
            >
              Submit
            </a-button>
          </div>
        </div>
      </template>
    </a-modal>

    <span class="ant-col-24 ant-form-item-label">Video</span>

    <div class="flex gap-2 ant-col-24" data-testid="document-form-video-controls">
      <a-button @click="openLinkModal" data-testid="document-form-video-add-link-button">
        <a-icon type="link" />Add Via Link
      </a-button>
      <a-upload
        :action="null"
        :showUploadList="false"
        :customRequest="customRequestHandler"
        @change="handleFileChange"
        accept="video/*"
      >
        <a-button data-testid="document-form-video-pick-file-button"> <a-icon type="upload" /> Pick File </a-button>
      </a-upload>
    </div>
    <video
      v-if="link"
      class="ant-col-24 mt-5"
      :style="{ height: '336px' }"
      :src="link"
      controls
      data-testid="document-form-video-preview"
    ></video>
  </a-spin>
</template>

<script>
import { Alert, Button, Icon, Input, notification, Spin, Upload } from 'ant-design-vue'

export default {
  components: {
    'a-icon': Icon,
    'a-alert': Alert,
    'a-input': Input,
    'a-button': Button,
    'a-upload': Upload,
    'a-spin': Spin
  },
  props: {
    file: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      visibleLinkModal: false,

      link: undefined,
      uploadedLink: undefined,

      loading: false
    }
  },
  watch: {
    file: {
      handler(newVal) {
        if (newVal) this.link = newVal
      },
      immediate: true
    }
  },
  methods: {
    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    closeLinkModal() {
      this.visibleLinkModal = false
      this.uploadedLink = undefined
    },
    openLinkModal() {
      this.visibleLinkModal = true
    },
    customRequestHandler(options) {
      options.onSuccess('success', options.file)
    },
    async uploadLink() {
      try {
        if (!this.uploadedLink) {
          return
        }

        const videoExtensions = /\.(mp4|webm|ogg|mov|avi|flv|wmv|m4v)$/i
        if (!videoExtensions.test(this.uploadedLink)) {
          this.showNotification(
            'error',
            'Error',
            'Wrong link! Please double-check that you’re attaching either a direct video file/resource link. Other link types aren’t supported in the app right now, so you’ll need to attach the video as an actual file upload instead.'
          )
          return
        }

        const response = await fetch(this.uploadedLink)

        if (!response.ok) {
          throw new Error(
            'Wrong link! Please double-check that you’re attaching either a direct video file/resource link. Other link types aren’t supported in the app right now, so you’ll need to attach the video as an actual file upload instead.'
          )
        }

        const blob = await response.blob()

        const fileName = this.uploadedLink.split('/').pop() || 'video'

        const file = new File([blob], fileName, { type: blob.type })

        this.link = this.handleFile(file)

        this.uploadingFile(file)

        this.closeLinkModal()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      }
    },
    handleFile(file) {
      return URL.createObjectURL(file)
    },
    handleFileChange({ file }) {
      this.uploadingFile(file.originFileObj)
      this.link = this.handleFile(file.originFileObj)
    },
    async uploadingFile(file) {
      this.loading = true
      try {
        const data = await this.$store.dispatch('uploadFiles', {
          files: [file]
        })

        if (data.error_code && data.error_code !== '0') {
          notification.warning({
            message: 'Upload Error',
            description: data.mag ?? 'Please try again to upload document'
          })
          return
        }
        if (data.ids.length) {
          this.$emit('save', data.ids[0])
        }
      } catch (err) {
        this.showNotification('error', 'Error', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped></style>
